import { CLOZE_DECK } from '../data/cloze-deck.ts';
import type { ClozeItem, ClozeState, GrammarTopic } from '../data/cloze-types.ts';
import { CEFR_ORDER } from '../data/candos.ts';
import {
  loadSettings, logSession, isoDate,
  getItemState, putItemState, getAllItemStates,
} from '../data/stores.ts';

const TOPIC_LABEL: Record<GrammarTopic, string> = {
  'pronoun-man': 'Le pronom « man »',
  'word-order-v2': 'Ordre des mots V2',
  'definite-suffix': 'Article défini postposé',
  'negation': 'Placement de « ikke »',
  'reflexive-seg': 'Pronoms réfléchis (seg/meg/deg)',
  'modal-verbs': 'Verbes modaux',
  'infinitive-å': 'Marqueur infinitif « å »',
  'adjective-agreement': 'Accord de l’adjectif',
  'plural-nouns': 'Pluriel des noms',
  'possessive': 'Possessifs (min/mi/mitt/mine)',
  'relative-som': 'Relatif « som »',
  'past-tense': 'Passé (preteritum / perfektum)',
};

function normalize(s: string): string {
  return s.trim().toLowerCase().replace(/[«»".,!?;:]/g, '');
}

function shuffled<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

export function renderGrammaire(): HTMLElement {
  const root = document.createElement('section');
  root.className = 'flex flex-col items-center gap-6 max-w-2xl mx-auto';
  root.innerHTML = '<p class="text-[color:var(--color-ink-muted)] mt-12">Chargement de la session…</p>';
  initSession(root);
  return root;
}

async function initSession(root: HTMLElement): Promise<void> {
  const settings = await loadSettings();
  const levelIdx = CEFR_ORDER.indexOf(settings.currentLevel);
  const allowed = new Set(CEFR_ORDER.slice(0, levelIdx + 1));
  const itemsForLevel = CLOZE_DECK.filter((c) => allowed.has(c.cefr as never));

  const states = await getAllItemStates<ClozeState>('cloze-state');
  const todayIso = isoDate();

  // Prioritise: not seen today + not yet mastered → mastered (cycled less)
  const queue = shuffled(itemsForLevel)
    .map((it) => ({ it, st: states.get(it.id) }))
    .filter(({ st }) => !st || st.lastSeen !== todayIso)
    .sort((a, b) => (a.st?.mastered ? 1 : 0) - (b.st?.mastered ? 1 : 0))
    .slice(0, settings.dailyClozeLimit)
    .map((x) => x.it);

  if (queue.length === 0) {
    root.innerHTML = '';
    root.append(renderDone(itemsForLevel.length, settings.dailyClozeLimit, 0, 0));
    return;
  }

  let idx = 0;
  let correctCount = 0;

  function next(wasCorrect: boolean | null): void {
    if (wasCorrect === true) correctCount++;
    idx++;
    if (idx >= queue.length) {
      root.innerHTML = '';
      root.append(renderDone(itemsForLevel.length, queue.length, idx, correctCount));
      void logSession({ track: 'cloze', count: queue.length, score: correctCount / queue.length });
      return;
    }
    show();
  }

  function show(): void {
    root.innerHTML = '';
    const item = queue[idx]!;
    root.append(renderHeader(idx, queue.length));
    root.append(renderItem(item, next));
  }

  show();
}

function renderHeader(idx: number, total: number): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'w-full';
  const pct = Math.round(((idx) / total) * 100);
  wrap.innerHTML = `
    <div class="flex items-center justify-between text-sm text-[color:var(--color-ink-muted)] mb-2">
      <span>Phrase ${idx + 1} sur ${total}</span>
      <span>${pct}%</span>
    </div>
    <div class="w-full h-1 rounded-full bg-[color:var(--color-border)]">
      <div class="h-full rounded-full bg-[color:var(--color-fjord-500)] transition-all" style="width:${pct}%"></div>
    </div>
  `;
  return wrap;
}

function renderItem(item: ClozeItem, onNext: (correct: boolean | null) => void): HTMLElement {
  const card = document.createElement('div');
  card.className = 'w-full rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-6 flex flex-col gap-4';

  const topic = document.createElement('p');
  topic.className = 'text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded self-start';
  topic.style.background = 'var(--tint-peach-bg)';
  topic.style.color = 'var(--tint-peach-ink)';
  topic.textContent = `${TOPIC_LABEL[item.topic]} · ${item.cefr}`;
  card.append(topic);

  const sentence = document.createElement('p');
  sentence.className = 'text-2xl leading-relaxed font-medium';
  sentence.innerHTML = item.template.replace(
    '___',
    '<span class="inline-block min-w-[4ch] px-2 py-0.5 rounded border-b-2 border-[color:var(--color-fjord-500)] text-[color:var(--color-fjord-700)]" data-blank>____</span>'
  );
  card.append(sentence);

  // Multiple choice — shuffle answers (first one) + distractors
  const canonical = item.answers[0]!;
  const choices = shuffled([canonical, ...item.distractors]);
  const choicesWrap = document.createElement('div');
  choicesWrap.className = 'grid grid-cols-2 gap-2';
  let answered = false;
  for (const choice of choices) {
    const btn = document.createElement('button');
    btn.className = 'px-4 py-3 rounded-lg border border-[color:var(--color-border)] text-base hover:bg-[color:var(--color-fjord-50)] transition-colors text-left';
    btn.textContent = choice;
    btn.addEventListener('click', () => {
      if (answered) return;
      answered = true;
      const correct = item.answers.map(normalize).includes(normalize(choice));
      void recordAttempt(item.id, correct);
      revealFeedback(card, item, choice, correct, onNext);
      // Mark all buttons
      for (const b of choicesWrap.querySelectorAll('button')) {
        const t = (b as HTMLButtonElement).textContent ?? '';
        const isAns = item.answers.map(normalize).includes(normalize(t));
        if (isAns) b.classList.add('bg-[color:var(--color-sage)]/30', 'border-[color:var(--color-sage)]');
        else if (t === choice) b.classList.add('bg-[color:var(--color-rorbu)]/20', 'border-[color:var(--color-rorbu)]');
        (b as HTMLButtonElement).disabled = true;
      }
    });
    choicesWrap.append(btn);
  }
  card.append(choicesWrap);

  // Hint
  const hintBtn = document.createElement('button');
  hintBtn.className = 'self-start text-xs text-[color:var(--color-fjord-600)] hover:underline';
  hintBtn.textContent = '💡 Indice';
  hintBtn.addEventListener('click', () => {
    hintBtn.replaceWith(makeHintBox(item.hint));
  });
  card.append(hintBtn);

  return card;
}

function makeHintBox(text: string): HTMLElement {
  const p = document.createElement('p');
  p.className = 'text-xs text-[color:var(--color-ink-muted)] px-3 py-2 rounded bg-[color:var(--tint-butter-bg)] text-[color:var(--tint-butter-ink)]';
  p.textContent = `💡 ${text}`;
  return p;
}

function revealFeedback(
  card: HTMLElement, item: ClozeItem, given: string, correct: boolean,
  onNext: (correct: boolean) => void,
): void {
  const feedback = document.createElement('div');
  feedback.className = 'mt-2 p-4 rounded-lg ' + (correct
    ? 'bg-[color:var(--color-sage)]/15 border border-[color:var(--color-sage)]'
    : 'bg-[color:var(--color-rorbu)]/10 border border-[color:var(--color-rorbu)]');

  const filled = item.template.replace('___', `<strong>${item.answers[0]}</strong>`);
  feedback.innerHTML = `
    <p class="text-base">${correct ? '✓ Bien joué&nbsp;!' : `Réponse&nbsp;: <strong>${item.answers[0]}</strong>`}</p>
    <p class="text-sm mt-2">${filled}</p>
    <p class="text-sm text-[color:var(--color-ink-muted)] mt-1 italic">${item.french}</p>
    ${correct ? '' : `<p class="text-xs mt-2">Vous avez répondu : <em>${given}</em></p>`}
  `;
  card.append(feedback);

  const nextBtn = document.createElement('button');
  nextBtn.className = 'self-end px-5 py-2 rounded-lg bg-[color:var(--color-fjord-600)] text-white text-sm font-medium hover:bg-[color:var(--color-fjord-700)] transition-colors';
  nextBtn.textContent = 'Suivante →';
  nextBtn.addEventListener('click', () => onNext(correct));
  card.append(nextBtn);
}

async function recordAttempt(id: string, correct: boolean): Promise<void> {
  const prev = await getItemState<ClozeState>('cloze-state', id);
  const next: ClozeState = {
    attempts: (prev?.attempts ?? 0) + 1,
    correct: (prev?.correct ?? 0) + (correct ? 1 : 0),
    lastSeen: isoDate(),
    mastered: prev?.mastered ?? false,
  };
  // 3 consecutive correct on different days → mastered. Approximation here:
  // mastered if correct rate >= 80% over >=3 attempts.
  if (next.attempts >= 3 && next.correct / next.attempts >= 0.8) next.mastered = true;
  await putItemState('cloze-state', id, next);
}

function renderDone(_totalAvailable: number, _planned: number, answered: number, correct: number): HTMLElement {
  const el = document.createElement('div');
  el.className = 'flex flex-col items-center gap-4 mt-12';
  const pct = answered > 0 ? Math.round((correct / answered) * 100) : 0;
  el.innerHTML = `
    <div class="w-16 h-16 rounded-full bg-[color:var(--color-sage)] text-white flex items-center justify-center text-2xl">✓</div>
    <h2 class="text-2xl font-semibold">Session de grammaire terminée</h2>
    ${answered > 0
      ? `<p class="text-[color:var(--color-ink-muted)]">${correct} sur ${answered} correctes — ${pct}%</p>`
      : `<p class="text-[color:var(--color-ink-muted)]">Toutes les phrases du jour ont déjà été vues. Revenez demain&nbsp;!</p>`}
    <a href="#/accueil" class="text-sm text-[color:var(--color-fjord-600)] hover:underline">← Retour à l'accueil</a>
  `;
  return el;
}
